import axios from "axios";
import { ActionContext, Module } from "vuex";

import { Match, Token } from "@/types";
import { analytics } from "@/analytics";
import { auth as manager } from "@/auth";
import { RootState } from "@/store/root";
import { auth } from "@/store/auth";

export interface MatchesState {
  list?: { [key: string]: Match[] };
}

type RawToken = { tok: string; cpos: [number, number] };

export const matches: Module<MatchesState, RootState> = {
  state: {
    list: undefined
  },
  namespaced: true,
  mutations: {
    UPDATE(state: MatchesState, value: { [key: string]: Match[] }) {
      state.list = Object.freeze(value);
    },
    CLEAR(state: MatchesState) {
      state.list = undefined;
    }
  },
  actions: {
    async update(
      context: ActionContext<MatchesState, RootState>,
      status = true
    ) {
      if (analytics.enabled) {
        analytics.record({ name: "analysis" });
      }

      // eslint-disable-next-line no-control-regex
      const text = context.rootState.raw.replace(/[^\x00-\x7F]/g, "");

      try {
        if (status) context.commit("UPDATE_STATUS", "loading", { root: true });

        const response = await axios.post(
          (context.rootState as any).settings.current.endpoints.analysis,
          text,
          {
            headers: manager.enabled ? await manager.headers() : {}
          }
        );

        const tokens = response.data.tokens.map(
          (token: RawToken, index: number, arr: RawToken[]) => {
            const next = index < arr.length - 1 ? arr[index + 1].cpos[0] : -1;

            return {
              index,
              text: token.tok.replace(/\\n/g, "\n"),
              span: token.cpos,
              delimiter: next != -1 ? text.substring(token.cpos[1], next) : ""
            };
          }
        );

        const charToToken = Object.assign(
          {},
          ...tokens.map((token: Token, index: number) => ({
            [token.span[0]]: index
          }))
        );

        const start = response.data.meta.blocks.map(
          (
            block: { bcharpos: number },
            index: number,
            arr: { bcharpos: number }[]
          ) =>
            charToToken[block.bcharpos] ||
            (index > 0
              ? Object.entries(charToToken).sort(
                  ([a], [b]) =>
                    Math.abs(block.bcharpos - +a) -
                    Math.abs(block.bcharpos - +b)
                )[0][1]
              : 0)
        );

        const end = [...start.slice(1), tokens.length];

        const blocks = start.map((spos: number, index: number) =>
          Array.from({ length: end[index] - spos }, (_, i) => i + spos)
        );

        context.commit("UPDATE", response.data.content);
        context.commit("UPDATE_TIME", response.data.meta.computetime, {
          root: true
        });
        context.commit("UPDATE_TEXT", text, { root: true });
        context.commit("UPDATE_BLOCKS", blocks, { root: true });
        context.commit("UPDATE_TOKENS", tokens, { root: true });

        if (status) context.commit("UPDATE_STATUS", "idle", { root: true });
      } catch (e) {
        context.commit("UPDATE_STATUS", "failed", { root: true });
        throw e;
      }
    },
    clear(context: ActionContext<MatchesState, RootState>) {
      context.commit("CLEAR");
    }
  },
  getters: {
    parsed(state: MatchesState, _, rootState: RootState) {
      return (type: string) => {
        if (!rootState.tokens || !state.list) return [];

        const result = new Array(rootState.tokens.length);

        for (let match of state.list[type] || []) {
          for (
            let index = match.concept.text.tokpos[0];
            index < match.concept.text.tokpos[1];
            index++
          ) {
            result[index] = [match, ...(result[index] || [])];
          }
        }

        return Object.freeze(result);
      };
    },
    flatten(state: MatchesState, _, rootState: RootState) {
      if (!rootState.tokens || !state.list) return [];

      const result = new Array(rootState.tokens.length);

      for (let list of Object.values(state.list)) {
        for (let match of list) {
          for (
            let index = match.concept.text.tokpos[0];
            index < match.concept.text.tokpos[1];
            index++
          ) {
            result[index] = [...(result[index] || []), match];
          }
        }
      }

      return result;
    }
  }
};
